import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Panel } from "./ui";
import { StockDrillDialog } from "./StockDrillDialog";
import { Boxes, MapPin, RefreshCw, Wrench, Undo2, Inbox as InboxIcon, ChevronRight } from "lucide-react";

const Stat = ({ icon: Icon, label, value, hint }) => (
  <div className="bg-white border border-slate-200 rounded-sm p-5" style={{ borderTop: "3px solid #00857C" }}>
    <div className="flex items-center justify-between">
      <div className="overline text-slate-500">{label}</div>
      <Icon className="h-4 w-4 text-[#00857C]" />
    </div>
    <div className="font-head text-3xl font-bold text-[#0B2545] mt-2">{value}</div>
    {hint && <div className="text-xs text-slate-400 mt-1">{hint}</div>}
  </div>
);

const STATUS_ORDER = ["in_bin", "in_transit_to_regional", "at_regional", "in_transit_to_site", "at_site", "installed", "active", "return_in_transit", "returned", "written_off"];

export default function Dashboard() {
  const [d, setD] = useState(null);
  const [drillStatus, setDrillStatus] = useState(null);

  useEffect(() => { api.get("/dashboard").then((r) => setD(r.data)); }, []);
  if (!d) return null;

  return (
    <div>
      <PageHeader title="Operations Dashboard" subtitle="Live view of consignment stock across the network." />
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <Stat icon={Boxes} label="Total Units" value={d.units.total} hint="serialised consignment units" />
        <Stat icon={MapPin} label="Regional WHs" value={d.regionals} hint={`${d.sites} sites/technicians`} />
        <Stat icon={RefreshCw} label="Replenishment" value={d.pending_replenishment} hint="pending requests" />
        <Stat icon={Wrench} label="Awaiting IP2" value={d.installed_pending_activation} hint="installed, not activated" />
        <Stat icon={Undo2} label="Returns" value={d.returns_in_transit} hint="in transit" />
        <Stat icon={InboxIcon} label="AX Emails" value={d.inbox} hint="IP1 + IP2 received" />
      </div>
      <Panel title="Stock by Status">
        {d.units.total === 0 ? (
          <div className="text-sm text-slate-400 py-6">No stock yet. Create a Main Consignment Bin under Locations, then trigger IP1 in the AX Inbox to load stock.</div>
        ) : (
          <>
            <div className="text-xs text-slate-400 mb-3">Click a status to drill down by location, then by serial.</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {STATUS_ORDER.filter((s) => d.units.by_status[s]).map((s) => (
                <button
                  key={s}
                  data-testid={`drill-status-${s}`}
                  onClick={() => setDrillStatus(s)}
                  className="text-left border border-slate-200 rounded-sm p-3 hover:border-[#00857C] hover:shadow-sm transition-colors group"
                >
                  <div className="flex items-center justify-between">
                    <div className="text-xs uppercase tracking-wide text-slate-500">{s.replace(/_/g, " ")}</div>
                    <ChevronRight className="h-3.5 w-3.5 text-slate-300 group-hover:text-[#00857C] transition-colors" />
                  </div>
                  <div className="font-head text-2xl font-bold text-[#0B2545]">{d.units.by_status[s]}</div>
                </button>
              ))}
            </div>
          </>
        )}
      </Panel>

      <StockDrillDialog open={!!drillStatus} status={drillStatus} onClose={() => setDrillStatus(null)} />
    </div>
  );
}
